import type { TFunction } from '@tupai/i18n/react';
import type { parentResources } from '@tupai/i18n/parent';
import { ApiError } from '@/lib/api';

type ErrorKey = keyof (typeof parentResources)['en']['common'] & `err${string}`;

// Every screen that used to render a raw `err.message` goes through this, so the API's English never reaches a Malay screen.
const API_ERROR_KEYS: Record<string, ErrorKey> = {
  seat_limit_reached: 'errSeatLimit',
  // No dedicated fallback string — GateNoticeModal is this code's real
  // surface (it needs the max-tier-specific copy); this generic mapper is
  // only a backstop for any OTHER caller that renders apiErrorMessage()
  // directly instead of the modal.
  seat_limit_reached_max_tier: 'errSeatLimit',
  trial_seat_locked: 'errTrialSeatLocked',
  feature_blocked: 'errFeatureBlocked',
  upgrade_required: 'errUpgradeRequired',
  quota_exhausted: 'errQuotaExhausted',
  student_already_linked: 'errStudentAlreadyLinked',
  // parent-api emits student_already_linked_elsewhere from both seat-attach
  // paths (pairing approve and redeem). It was unmapped, so apiErrorMessage
  // fell through to the generic "something went wrong" and a Malay parent
  // never saw translated copy for the commonest linking conflict
  // (API-PXC-003). already_in_household is kept for older payloads.
  student_already_linked_elsewhere: 'errAlreadyInHousehold',
  already_in_household: 'errAlreadyInHousehold',
  cooldown: 'errCooldown',
  not_started: 'errNotStarted',
  no_household: 'errNoHousehold',
  'No household': 'errNoHousehold',
  'Code has expired': 'errCodeExpired',
  'Code not found': 'errCodeNotFound',
  'This phone number is already in use': 'errPhoneInUse',
  phone_in_use: 'errPhoneInUse',
  account_not_found: 'errAccountNotFound',
  Unauthorised: 'errUnauthorised',
  consent_version_outdated: 'errConsentOutdated',
};

export function apiErrorMessage(err: unknown, t: TFunction<'common'>, fallback?: string): string {
  if (err instanceof ApiError && err.code) {
    const key = API_ERROR_KEYS[err.code];
    if (key) return t(key);
  }
  return fallback ?? t('somethingWentWrong');
}
